import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { HiOutlineLink, HiOutlineArrowLeft, HiOutlineArrowRight, HiOutlineExclamationCircle } from "react-icons/hi"
import { LuUnlink } from "react-icons/lu"

import AvatarProfile from "components/ui/Avatar/AvatarProfile"
import { Badge, Notification, toast, Tooltip, Button } from "components/ui"
import { apiGetTicketById, apiLinkTicket } from "services/TicketsService"
import { inventoryStatusColor } from "utils/storeHube/statusColor"
import { setSelectedTicket } from "../store/dataSlice"
import AsideCard from "./AsideCard"

// Card unica de tareas vinculadas, en el aside y debajo de Detalles.
//
// Antes esto vivia en dos lugares con el mismo nombre y distinto comportamiento:
// la pestana "Tareas vinculadas" listaba las derivadas y abria en pestana nueva,
// y la card del aside mostraba solo la principal y navegaba en la misma pestana.
// Desde una derivada no habia forma de ver a sus hermanas.
//
// Ahora el arbol completo se arma desde cualquier nodo: el ticket trae `parent_id`
// e `incidents`, y pidiendo la principal se obtienen todas sus derivadas. No hace
// falta endpoint nuevo.
//
// Props:
// - ticket: el ticket abierto (para leer id, incidents y datos de la principal)
// - parentId: id de la principal, null si el ticket abierto ya es la principal
// - onOpenParent: navegacion a la principal (la resuelve TicketDetail)
// - onOpenTicket: navegacion a cualquier vinculada
// - canUnlink: muestra la accion de desvincular en las derivadas

const detalleCache = new Map()

// A partir de esta cantidad de filas la lista scrollea en vez de estirar la
// columna. En produccion el 95% de las tareas tiene 4 vinculadas o menos, asi
// que el scroll casi nunca aparece; existe para la cola larga (el maximo real
// es 39).
const MAX_FILAS_SIN_SCROLL = 5
const LOTE_DETALLES = 6

const NodoSkeleton = () => (
    <div className="flex items-center gap-3 px-2 py-2.5">
        <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 animate-pulse shrink-0" />
        <div className="flex-1 min-w-0 space-y-1.5">
            <div className="h-3 w-2/3 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
            <div className="h-2.5 w-1/3 rounded bg-gray-100 dark:bg-gray-700/60 animate-pulse" />
        </div>
    </div>
)

// Un ticket llega con formas distintas segun de donde venga (stub de `incidents`,
// respuesta de apiGetTicketById, o el ticket abierto en el store).
const normalizar = (raw, id) => {
    if (!raw) return { id, titulo: null, statusId: null, asignado: null }
    const p = raw.props || raw
    return {
        id: p.id ?? raw.id ?? id,
        titulo:
            p.ticket_category_primary?.name ||
            p.ticket_category_secondary?.name ||
            raw.title ||
            (raw.description || p.description
                ? (raw.description || p.description).replace(/<[^>]+>/g, "").trim().slice(0, 80)
                : null),
        statusId: p.ticket_status?.id ?? p.ticket_status_id ?? raw.ticket_status_id ?? null,
        asignado: p.operator || p.owner || null,
        tienda: p.store?.name || null,
    }
}

const Fila = ({
    nodo,
    esActual,
    direccion,
    onOpen,
    onUnlink,
    fallo,
    cargando,
    innerRef,
    className = "",
}) => {
    const { t } = useTranslation()
    if (cargando) return <NodoSkeleton />

    const statusMeta = nodo.statusId ? inventoryStatusColor[nodo.statusId] : null
    const nombre = nodo.asignado
        ? `${nodo.asignado.name || ""} ${nodo.asignado.lastname || ""}`.trim()
        : null
    const Flecha = direccion === "arriba" ? HiOutlineArrowLeft : HiOutlineArrowRight

    const contenido = (
        <>
            {nodo.asignado ? (
                <AvatarProfile user={nodo.asignado} size={32} className="shrink-0" />
            ) : fallo ? (
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-amber-50 dark:bg-amber-900/20 text-amber-500 shrink-0">
                    <HiOutlineExclamationCircle className="w-4 h-4" />
                </div>
            ) : (
                <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 shrink-0" />
            )}

            <div className="flex-1 min-w-0">
                <div className="flex items-center flex-wrap gap-1.5">
                    <span className="text-[11.5px] font-semibold tabular-nums text-indigo-600 dark:text-indigo-400">
                        #{nodo.id}
                    </span>
                    {statusMeta && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10.5px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                            <Badge innerClass={statusMeta.dotClass} />
                            {statusMeta.label}
                        </span>
                    )}
                    {esActual && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10.5px] font-semibold bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">
                            {t("linked_you_are_here", "Estás aquí")}
                        </span>
                    )}
                </div>
                {nodo.titulo ? (
                    // Dos lineas en vez de truncar: la indentacion del arbol se come
                    // ancho, y los titulos de categoria son largos ("AUDITORIA TIENDAS
                    // PAIS (1 vez por mes)"). El alto sobra en la columna lateral.
                    <p className="mt-0.5 text-[12.5px] font-medium text-gray-800 dark:text-gray-100 leading-tight line-clamp-2">
                        {nodo.titulo}
                    </p>
                ) : (
                    <p className="mt-0.5 text-[12.5px] italic text-gray-400 dark:text-gray-500 leading-tight truncate">
                        {fallo
                            ? t("linked_fetch_failed", "No se pudo cargar el detalle")
                            : t("linked_no_description", "Sin descripción")}
                    </p>
                )}
                {nombre && (
                    <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 truncate">
                        {nombre}
                    </p>
                )}
            </div>

            {!esActual && (
                <Flecha className="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400 shrink-0 group-hover:translate-x-0.5 transition-transform" />
            )}
        </>
    )

    // La actual no navega a ningun lado: es donde ya estas.
    if (esActual) {
        return (
            <div
                ref={innerRef}
                className={`flex items-center gap-3 px-2 py-2.5 rounded-lg bg-indigo-50/70 dark:bg-indigo-900/20 ring-1 ring-indigo-100 dark:ring-indigo-900/40 ${className}`}
                aria-current="true"
            >
                {contenido}
            </div>
        )
    }

    return (
        <div className={`group relative flex items-center gap-1 pr-1 rounded-lg hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors ${className}`}>
            <button
                type="button"
                onClick={() => onOpen(nodo.id)}
                className="flex items-center gap-3 flex-1 min-w-0 px-2 py-2.5 text-left rounded-lg cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
            >
                {contenido}
            </button>
            {onUnlink && (
                <Tooltip title={t("linked_delete")}>
                    <button
                        type="button"
                        onClick={onUnlink(nodo.id)}
                        className="shrink-0 inline-flex items-center justify-center w-11 h-11 md:w-8 md:h-8 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 opacity-100 md:opacity-0 md:group-hover:opacity-100 focus-visible:opacity-100 transition-all"
                        aria-label={`${t("linked_delete")} #${nodo.id}`}
                    >
                        <LuUnlink className="w-4 h-4" />
                    </button>
                </Tooltip>
            )}
        </div>
    )
}

const TicketLinkedCard = ({
    ticket,
    parentId = null,
    onOpenParent,
    onOpenTicket,
    canUnlink = true,
}) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const listaRef = useRef(null)
    const filaActualRef = useRef(null)
    const tocadoRef = useRef(false)
    const programaticoRef = useRef(false)

    // El ticket llega con los escalares planos (`id`, `parent_id`) y las relaciones
    // anidadas en `props`. Leer el id de `props` devuelve undefined y ninguna fila
    // queda marcada como la actual.
    const rel = ticket?.props || {}
    const ticketId = ticket?.id ?? rel.id
    const incidentsPropios = useMemo(
        () => rel.incidents || ticket?.incidents || [],
        [rel.incidents, ticket?.incidents]
    )

    // Con parentId el arbol cuelga de la principal, que hay que pedir para saber
    // su titulo y quienes son las hermanas. Sin parentId, la principal es esta.
    const [principalRaw, setPrincipalRaw] = useState(() =>
        parentId ? detalleCache.get(parentId) || null : ticket
    )
    const [cargandoPrincipal, setCargandoPrincipal] = useState(!!parentId && !detalleCache.get(parentId))
    const [falloPrincipal, setFalloPrincipal] = useState(false)

    useEffect(() => {
        // El `fallo` se limpia en los tres caminos y no solo antes del fetch: si no,
        // un error en una tarea deja el icono de alerta prendido en la siguiente.
        if (!parentId) {
            setPrincipalRaw(ticket)
            setCargandoPrincipal(false)
            setFalloPrincipal(false)
            return
        }
        if (detalleCache.has(parentId)) {
            setPrincipalRaw(detalleCache.get(parentId))
            setCargandoPrincipal(false)
            setFalloPrincipal(false)
            return
        }
        let activo = true
        setCargandoPrincipal(true)
        setFalloPrincipal(false)
        apiGetTicketById(parentId)
            .then(({ data }) => {
                if (!activo) return
                const payload = data?.data || data
                detalleCache.set(parentId, payload)
                setPrincipalRaw(payload)
            })
            .catch(() => activo && setFalloPrincipal(true))
            .finally(() => activo && setCargandoPrincipal(false))
        return () => {
            activo = false
        }
    }, [parentId, ticket])

    const principalProps = principalRaw?.props || principalRaw || {}
    const derivadasStubs = parentId ? principalProps.incidents || [] : incidentsPropios

    const [detalles, setDetalles] = useState(() => {
        const inicial = {}
        derivadasStubs.forEach((d) => {
            if (detalleCache.has(d.id)) inicial[d.id] = detalleCache.get(d.id)
        })
        return inicial
    })

    const idsDerivadas = derivadasStubs.map((d) => d.id).join(",")

    // El ticket abierto viene del store, o sea recien traido: pisa lo que haya
    // guardado. Sin esto, cerrar una vinculada y volver al arbol la mostraba con
    // el estado viejo, porque nada vuelve a pedir lo que ya esta cacheado.
    //
    // Hay que escribir en los dos lados: el render lee `detalles[id] || cache`, asi
    // que un `detalles` viejo le gana a una cache fresca.
    useEffect(() => {
        if (!ticketId || !ticket) return
        detalleCache.set(ticketId, ticket)
        setDetalles((prev) => (ticketId in prev ? { ...prev, [ticketId]: ticket } : prev))
    }, [ticketId, ticket])

    useEffect(() => {
        const pendientes = derivadasStubs
            .map((d) => d.id)
            .filter((id) => id !== ticketId && !detalleCache.has(id))
        if (!pendientes.length) return
        let activo = true

        // De a tandas y no todas juntas: el maximo real en produccion son 39
        // derivadas en una misma tarea, y eso serian 39 GET simultaneos. Cada
        // tanda pinta apenas llega, asi que las primeras filas dejan de estar en
        // skeleton sin esperar a la ultima.
        const porTanda = async () => {
            for (let i = 0; i < pendientes.length && activo; i += LOTE_DETALLES) {
                const lote = pendientes.slice(i, i + LOTE_DETALLES)
                const res = await Promise.allSettled(lote.map((id) => apiGetTicketById(id)))
                if (!activo) return
                const updates = {}
                res.forEach((r, j) => {
                    const id = lote[j]
                    if (r.status === "fulfilled") {
                        const payload = r.value?.data?.data || r.value?.data
                        updates[id] = payload
                        if (payload) detalleCache.set(id, payload)
                    } else {
                        updates[id] = null
                    }
                })
                setDetalles((prev) => ({ ...prev, ...updates }))
            }
        }
        porTanda()

        return () => {
            activo = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsDerivadas, ticketId])

    const desvincular = async (id) => {
        try {
            await apiLinkTicket(id, { parent_id: null })
            detalleCache.delete(id)
            // Tambien la principal: su copia cacheada sigue teniendo la derivada
            // en `incidents`, y al abrir una hermana el arbol la mostraria de nuevo.
            if (ticketId) detalleCache.delete(ticketId)
            const copia = structuredClone(ticket)
            copia.props.incidents = incidentsPropios.filter((i) => i.id !== id)
            dispatch(setSelectedTicket(copia))
        } catch (e) {
            toast.push(
                <Notification
                    title={t("linked_delete_error", "No se pudo desvincular la tarea")}
                    type="danger"
                />
            )
        }
    }

    const confirmarDesvincular = (id) => async (e) => {
        e.preventDefault()
        e.stopPropagation()
        const key = await toast.push(
            <Notification title={t("linked_delete")} type="danger" duration={0}>
                <div>{t("linked_delete_message")}</div>
                <div className="flex text-right mt-3">
                    <Button
                        onClick={() => {
                            desvincular(id)
                            toast.remove(key)
                        }}
                        size="xs"
                        variant="solid"
                        className="mr-2"
                        color="red-500"
                    >
                        {t("confirm_capitalize")}
                    </Button>
                    <Button onClick={() => toast.remove(key)} size="xs">
                        {t("close")}
                    </Button>
                </div>
            </Notification>
        )
    }

    const abrir = (id) => {
        if (parentId && id === parentId && onOpenParent) return onOpenParent()
        onOpenTicket?.(id)
    }

    const principal = normalizar(principalRaw, parentId || ticketId)
    const principalEsActual = !parentId
    const derivadas = derivadasStubs.map((stub) => ({
        stub,
        nodo: normalizar(
            stub.id === ticketId ? ticket : detalles[stub.id] || detalleCache.get(stub.id),
            stub.id
        ),
        esActual: stub.id === ticketId,
        cargando: stub.id !== ticketId && !(stub.id in detalles) && !detalleCache.has(stub.id),
        fallo: detalles[stub.id] === null,
    }))

    const conScroll = derivadas.length > MAX_FILAS_SIN_SCROLL

    // Al entrar a una tarea, la lista se posiciona en la fila donde estas parado.
    // Corre en cada tanda de detalles porque las filas cambian de alto al dejar de
    // ser skeleton, pero deja de corregir apenas el usuario scrollea a mano.
    useEffect(() => {
        tocadoRef.current = false
    }, [ticketId])

    useEffect(() => {
        if (!conScroll || tocadoRef.current) return
        const cont = listaRef.current
        const fila = filaActualRef.current
        if (!cont || !fila || !cont.contains(fila)) return
        const desfase = fila.getBoundingClientRect().top - cont.getBoundingClientRect().top
        const objetivo = cont.scrollTop + desfase - 8
        if (Math.abs(objetivo - cont.scrollTop) < 2) return
        // Avisa al listener que el scroll que viene lo movimos nosotros, para que
        // no lo cuente como intervencion del usuario. El guard de arriba garantiza
        // que solo se marca cuando la posicion realmente cambia, asi que siempre
        // hay un evento que consuma la marca.
        programaticoRef.current = true
        cont.scrollTop = objetivo
    }, [conScroll, ticketId, idsDerivadas, detalles])

    if (!parentId && !derivadas.length) return null

    // La jerarquia se dibuja, no se etiqueta: la principal arriba y las derivadas
    // colgando de ella. Evita los encabezados "Principal" / "Derivadas" y se lee
    // igual desde cualquier nodo del arbol.
    //
    // La principal queda fuera del area que scrollea: aunque estes en la derivada
    // numero 30, seguis viendo de cual dependen todas.
    const cuerpo = (
        <div className="-mx-2">
            <Fila
                nodo={principal}
                esActual={principalEsActual}
                direccion="arriba"
                onOpen={abrir}
                cargando={cargandoPrincipal}
                fallo={falloPrincipal}
                innerRef={principalEsActual ? filaActualRef : null}
            />
            {derivadas.length > 0 && (
                <div
                    ref={listaRef}
                    onScroll={() => {
                        if (programaticoRef.current) {
                            programaticoRef.current = false
                            return
                        }
                        tocadoRef.current = true
                    }}
                    className={`pl-4 mt-0.5 ${
                        conScroll ? "max-h-[380px] overflow-y-auto pr-1" : ""
                    }`}
                >
                    {derivadas.map(({ stub, nodo, esActual, cargando, fallo }, i) => (
                        <div key={stub.id} className="relative">
                            {/* Tronco por fila en vez de uno solo absoluto: las filas no
                                miden lo mismo (el titulo puede ir a dos lineas), asi que
                                un tronco unico con `bottom` fijo queda colgando. El de la
                                ultima frena en su codo. Todo va en % para que el codo
                                caiga en el avatar, que `items-center` deja al medio. */}
                            <span
                                className={`absolute left-[-10px] top-0 w-px bg-gray-200 dark:bg-gray-700 ${
                                    i === derivadas.length - 1 ? "h-1/2" : "bottom-0"
                                }`}
                                aria-hidden="true"
                            />
                            <span
                                className="absolute left-[-10px] top-1/2 w-2.5 h-px bg-gray-200 dark:bg-gray-700"
                                aria-hidden="true"
                            />
                            <Fila
                                nodo={nodo}
                                esActual={esActual}
                                direccion="abajo"
                                onOpen={abrir}
                                onUnlink={canUnlink && !parentId && !esActual ? confirmarDesvincular : null}
                                cargando={cargando}
                                fallo={fallo}
                                innerRef={esActual ? filaActualRef : null}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    )

    return (
        <AsideCard
            icon={<HiOutlineLink className="w-4 h-4 text-gray-500 dark:text-gray-400" />}
            title={t("linked_tasks")}
            headerExtra={
                derivadas.length > 0 ? (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-gray-100 dark:bg-gray-700 text-[10.5px] font-semibold text-gray-600 dark:text-gray-300 tabular-nums">
                        {derivadas.length + (parentId ? 1 : 0)}
                    </span>
                ) : null
            }
            defaultExpanded={true}
        >
            {cuerpo}
        </AsideCard>
    )
}

export default TicketLinkedCard
